import { ApsCandidate, ApsSource } from './types';
import { pickMostRecentVisitDate } from './visitDateUtils';

export interface IcdEntry {
  code: string;
  description: string;
}

export interface DoctorGroup {
  key: string;
  source: ApsSource;
  // Shared doctor info (taken from the first candidate in the group)
  name: string;
  npi: string;
  specialization: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  information: string;
  facilityName: string;
  lastVisitDate: string;
  // Aggregated ICD codes from every candidate in the group
  icds: IcdEntry[];
  // Other sources where this same NPI also appears (cross-source overlap).
  // Populated by detecting NPI matches across groups of different sources.
  alsoIn: ApsSource[];
}

const groupKey = (c: ApsCandidate): string =>
  [
    c.source,
    `npi:${c.npi}`,
    `phone:${c.phoneNumber}`,
    `addr:${c.address}`,
  ].join('|');

/**
 * Groups candidates that share the same (source, NPI, phone, address) tuple
 * into a single "doctor" entry. Multiple Dx claims for the same provider
 * (one claim per visit / ICD code) collapse into one group whose `icds`
 * array carries every code + description.
 *
 * After grouping, computes `alsoIn` for each group by detecting NPI matches
 * across groups of different sources (so a Dx group whose NPI also appears
 * as an Rx group gets `alsoIn: ['Rx']`).
 *
 * Insertion order from the input is preserved.
 */
export const groupCandidatesByDoctor = (
  candidates: ApsCandidate[],
): DoctorGroup[] => {
  const map = new Map<string, DoctorGroup>();
  const order: string[] = [];

  for (const c of candidates) {
    const key = groupKey(c);
    const existing = map.get(key);
    const hasIcd = !!(c.code || c.description);

    if (existing) {
      if (hasIcd) {
        existing.icds.push({ code: c.code, description: c.description });
      }
      existing.lastVisitDate = pickMostRecentVisitDate(
        existing.lastVisitDate,
        c.visitDate,
      );
    } else {
      map.set(key, {
        key,
        source: c.source,
        name: c.name,
        npi: c.npi,
        specialization: c.specialization,
        phoneNumber: c.phoneNumber,
        address: c.address,
        city: c.city,
        state: c.state,
        zipcode: c.zipcode,
        information: c.information,
        facilityName: c.facilityName,
        lastVisitDate: c.visitDate,
        icds: hasIcd ? [{ code: c.code, description: c.description }] : [],
        alsoIn: [],
      });
      order.push(key);
    }
  }

  // Compute alsoIn: for each NPI , collect the set of
  // sources that share that NPI, then assign "the others" to each group.
  const sourcesByNpi = new Map<string, Set<ApsSource>>();
  for (const g of map.values()) {
    if (!g.npi) continue;
    const set = sourcesByNpi.get(g.npi) ?? new Set<ApsSource>();
    set.add(g.source);
    sourcesByNpi.set(g.npi, set);
  }
  for (const g of map.values()) {
    if (!g.npi) continue;
    const others = sourcesByNpi.get(g.npi);
    if (!others) continue;
    g.alsoIn = Array.from(others).filter(s => s !== g.source);
  }

  return order.map(k => map.get(k)!);
};
